import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import SessionFields from 'components/session/fields/SessionFields';
import SessionFieldsListeners from 'components/session/fields/SessionFieldsListeners';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import { userAvatarFields } from 'common/constants/query-fields/user';
import requestStatus from 'common/constants/status';
import getDataToSave from 'common/form/dataToSave';
import { updateSessionInitialValues } from 'common/form/initial-values/update';
import { getFieldsToUpdate } from 'common/form/utils';
import { convertLocalToUtcTime } from 'common/utils/date';
import { isValidId } from 'common/utils/id';
import { formDecorator } from 'common/utils/session';
import routes from 'common/routes';
import defaultSessionFormValidations from 'common/validations/form/defaultSession';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import AppBarContext from 'context/AppBarContext';
import clientPaymentSlice from 'store/modules/clientPayment';
import sessionSlice from 'store/modules/session';
import trainerSlice from 'store/modules/trainer';

const calculator = formDecorator;

function SessionEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['buttons', 'routes']);
  const pageTitle = t('session.edit', { ns: 'routes' });
  const { id } = useParams();
  const formRef = useRef(null);
  const [enableFormListeners, setEnableFormListeners] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const editStatus = useSelector((state) => state.session.status.edit);
  const session = useSelector((state) => state.session.data.currentSession);
  const status = useSelector((state) => state.session.status.general);
  const previousStatus = React.useRef(status);
  // `find` y `update` comparten el mismo status; sin este guard el find inicial rebotaría a la lista.
  const hasSubmitted = React.useRef(false);

  const sessionLoaded =
    session &&
    Object.keys(session).length > 0 &&
    session.id === id &&
    status === requestStatus.success;

  // Con un id inservible ("/sessions/edit/undefined") el servidor devuelve null, `sessionLoaded`
  // nunca se pone en true y la pantalla se quedaba en blanco para siempre, sin ningún mensaje: el
  // usuario no sabía si estaba cargando o si algo había fallado. Ahora ni siquiera se consulta y se
  // devuelve a la lista, que es el único sitio con sentido desde aquí.
  const hasValidId = isValidId(id);

  useEffect(() => {
    if (!hasValidId) {
      navigate(routes.listSession.path, { replace: true });
      return;
    }
    dispatch(sessionSlice.actions.find(id));
  }, []);

  // Los valores iniciales se fijan UNA SOLA VEZ, la primera vez que llega la sesión.
  //
  // Sin este cerrojo el formulario se reiniciaba solo mientras el admin lo estaba usando: al
  // cambiar la fecha se dispara `getSessionsInRange`, que comparte `status.general` con el `find`
  // de esta pantalla. Cuando esa consulta terminaba, el estado volvía a "success", `sessionLoaded`
  // pasaba otra vez a true y este efecto volvía a plantar los valores del servidor encima de lo
  // que el usuario acababa de elegir: la fecha nueva se revertía a la vieja y la hora se vaciaba,
  // dejando la sesión imposible de editar.
  const initialValuesSet = useRef(false);

  useEffect(() => {
    if (sessionLoaded && !initialValuesSet.current) {
      initialValuesSet.current = true;
      setInitialValues(updateSessionInitialValues(session));
    }
  }, [session, sessionLoaded]);

  useEffect(() => {
    if (sessionLoaded && initialValues?.trainer_id?.id) {
      dispatch(
        trainerSlice.actions.listAvailable({
          fields: userAvatarFields,
          filters: { ids: [Number(initialValues.trainer_id.id)] }
        })
      );
    }
  }, [dispatch, initialValues.trainer_id?.id, sessionLoaded]);

  useEffect(() => {
    if (sessionLoaded && Object.keys(initialValues).length > 0) {
      setEnableFormListeners(true);
    }
  }, [initialValues, sessionLoaded]);

  useEffect(() => {
    if (sessionLoaded && initialValues?.client_id?.value) {
      dispatch(clientPaymentSlice.actions.getLatest(Number(initialValues.client_id.value)));
    }
  }, [dispatch, initialValues.client_id?.value, sessionLoaded]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista después de una edición exitosa
  useEffect(() => {
    if (
      hasSubmitted.current &&
      previousStatus.current === requestStatus.loading &&
      status === requestStatus.success
    ) {
      navigate(routes.listSession.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      const dataToSave = getDataToSave(fieldsToUpdate, 'session');

      // `date`, `from` y `to` son campos DEL FORMULARIO, no del API: `UpdateSessionInput` solo
      // conoce `start_date` y `end_date`. Al enviarlos tal cual, la mutación fallaba con un error
      // de GraphQL y la sesión NUNCA cambiaba de horario aunque la pantalla dijera lo contrario.
      // Como los tres van juntos, si cambia cualquiera se recalculan las dos fechas completas a
      // partir de los valores actuales del formulario.
      if ('date' in dataToSave || 'from' in dataToSave || 'to' in dataToSave) {
        delete dataToSave.date;
        delete dataToSave.from;
        delete dataToSave.to;

        const base = values.date instanceof Date ? values.date : null;
        const from = values.from instanceof Date ? values.from : null;
        const to = values.to instanceof Date ? values.to : null;

        if (base && from && to) {
          const y = base.getFullYear();
          const mo = base.getMonth();
          const d = base.getDate();
          dataToSave.start_date = convertLocalToUtcTime(new Date(y, mo, d, from.getHours(), from.getMinutes()));
          dataToSave.end_date = convertLocalToUtcTime(new Date(y, mo, d, to.getHours(), to.getMinutes()));
        }
      }

      // El selector de plan trabaja con `payment_id`; el API espera `client_payment_id`.
      // `payment_label` solo existe para poder mostrar un plan ya consumido y no se guarda.
      if ('payment_id' in dataToSave) {
        dataToSave.client_payment_id = dataToSave.payment_id ? Number(dataToSave.payment_id) : null;
        delete dataToSave.payment_id;
      }
      delete dataToSave.payment_label;

      hasSubmitted.current = true;
      dispatch(sessionSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        decorators={[calculator]}
        initialValues={initialValues}
        onSubmit={onEdit}
        validate={defaultSessionFormValidations}
        render={({ form, handleSubmit, invalid, pristine, values }) => {
          formRef.current = form;

          return (
            <form onSubmit={handleSubmit}>
              <SessionFields isEditing values={values} disableClientSelection />
              <StyledButtonsContainer>
                <SecondaryButtonLoading
                  component={RouterLink}
                  disabled={editStatus === requestStatus.loading}
                  style={{ marginRight: '32px' }}
                  label={t('cancel', { ns: 'buttons' })}
                  to={routes.listSession.path}
                  type="button"
                />
                <PrimaryLoadingButton
                  disabled={invalid || pristine || editStatus === requestStatus.loading}
                  loading={editStatus === requestStatus.loading}
                  label={t('session.edit', { ns: 'buttons' })}
                  type="submit"
                />
              </StyledButtonsContainer>
              {enableFormListeners && <SessionFieldsListeners form={formRef?.current} />}
            </form>
          );
        }}
      />
    </>
  );
}

export default SessionEdit;
